import React, { createContext, useContext, useState, useEffect } from 'react';

const ConfigContext = createContext(null);

// Default translations structure (per language)
const defaultTranslations = {
    company: {
        name: "Company",
        productName: "truck",
        productNamePlural: "trucks",
        productType: "ice_cream"
    },
    onboarding: {
        enabled: true,
        slides: [],
        startButton: "Start"
    },
    tracker: {
        callButton: {
            text: "CALL"
        },
        radarButton: {
            activeText: "Active",
            inactiveText: "Notify me"
        }
    },
    dialogs: {},
    toasts: {},
    truckStatus: {},
    menu: { items: [] },
    footer: {},
    info: {}
};

// Shared config (language-independent)
const defaultShared = {
    branding: {
        primaryColor: "#f5323c",
        secondaryColor: "#62B5E5",
        accentColor: "#ffd700",
        textColor: "#4a3728",
        textColorLight: "#7a6a5a"
    },
    images: {
        logo: "/images/logo.png",
        callButtonImage: "/images/tracker/icon_button.png",
        callButtonOverlay: "/images/tracker/icon_roep.png",
        truckAvailableImage: "/images/tracker/icon_kar_available.png",
        truckUnavailableImage: "/images/tracker/icon_kar_notavailable.png"
    },
    links: {
        website: "",
        reservation: "",
        shop: "",
        jobs: ""
    }
};

// Full default config structure
const defaultConfig = {
    meta: {
        version: "2.0",
        defaultLanguage: "nl",
        supportedLanguages: ["nl", "fr", "en"]
    },
    shared: defaultShared,
    translations: {
        nl: defaultTranslations,
        fr: defaultTranslations,
        en: defaultTranslations
    }
};

/**
 * Check if config is in the new multi-language format
 */
function isMultiLanguageConfig(config) {
    return config && config.translations && config.meta;
}

/**
 * Migrate old single-language config to new multi-language format
 */
function migrateOldConfig(oldConfig) {
    // Extract branding and images to shared
    const shared = {
        branding: oldConfig.branding || defaultShared.branding,
        images: {
            logo: oldConfig.company?.logo || defaultShared.images.logo,
            callButtonImage: oldConfig.tracker?.callButton?.image || defaultShared.images.callButtonImage,
            callButtonOverlay: oldConfig.tracker?.callButton?.overlayImage || defaultShared.images.callButtonOverlay,
            truckAvailableImage: oldConfig.tracker?.truckMarker?.availableImage || defaultShared.images.truckAvailableImage,
            truckUnavailableImage: oldConfig.tracker?.truckMarker?.unavailableImage || defaultShared.images.truckUnavailableImage
        },
        links: oldConfig.links || defaultShared.links
    };

    // Create translation object from old config (assumed to be Dutch)
    const translation = {
        company: oldConfig.company || defaultTranslations.company,
        onboarding: oldConfig.onboarding || defaultTranslations.onboarding,
        tracker: oldConfig.tracker || defaultTranslations.tracker,
        dialogs: oldConfig.dialogs || defaultTranslations.dialogs,
        toasts: oldConfig.toasts || defaultTranslations.toasts,
        truckStatus: oldConfig.truckStatus || defaultTranslations.truckStatus,
        menu: oldConfig.menu || defaultTranslations.menu,
        footer: oldConfig.footer || defaultTranslations.footer,
        info: oldConfig.info || defaultTranslations.info
    };

    return {
        meta: {
            version: "1.0-migrated",
            defaultLanguage: "nl",
            supportedLanguages: ["nl"]
        },
        shared: shared,
        translations: {
            nl: translation
        }
    };
}

/**
 * Normalize config to ensure consistent structure
 */
function normalizeConfig(rawConfig) {
    if (!rawConfig) {
        return defaultConfig;
    }

    // Check if already in new format
    if (isMultiLanguageConfig(rawConfig)) {
        return deepMerge(defaultConfig, rawConfig);
    }

    // Migrate old format
    const migrated = migrateOldConfig(rawConfig);
    return deepMerge(defaultConfig, migrated);
}

export function ConfigProvider({ children }) {
    const [rawConfig, setRawConfig] = useState(defaultConfig);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetch('/config.json')
            .then(response => {
                if (!response.ok) {
                    throw new Error('Config not found');
                }
                return response.json();
            })
            .then(data => {
                const normalized = normalizeConfig(data);
                setRawConfig(normalized);
                setLoading(false);
            })
            .catch(err => {
                console.warn('Could not load config.json, using defaults:', err);
                setError(err);
                setLoading(false);
            });
    }, []);

    // Expose the full config structure
    const value = {
        config: rawConfig,
        meta: rawConfig.meta,
        shared: rawConfig.shared,
        translations: rawConfig.translations,
        loading,
        error,
        // Helper to get translation for a specific language
        getTranslation: (lang) => rawConfig.translations[lang] || rawConfig.translations[rawConfig.meta.defaultLanguage] || {},
        // Helper to update config (for admin editor)
        setConfig: setRawConfig
    };

    return (
        <ConfigContext.Provider value={value}>
            {children}
        </ConfigContext.Provider>
    );
}

export function useConfig() {
    const context = useContext(ConfigContext);
    if (!context) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }
    return context;
}

/**
 * Helper to get nested config value with dot notation
 * @param {object} obj - Object to traverse
 * @param {string} path - Dot-notation path (e.g., 'company.name')
 * @param {any} defaultValue - Default value if path not found
 */
export function getConfigValue(obj, path, defaultValue = '') {
    if (!obj || !path) return defaultValue;

    const keys = path.split('.');
    let value = obj;

    for (const key of keys) {
        if (value && typeof value === 'object' && key in value) {
            value = value[key];
        } else {
            return defaultValue;
        }
    }

    return value !== undefined ? value : defaultValue;
}

/**
 * Deep merge helper - merges source into target
 */
function deepMerge(target, source) {
    if (!source) return target;
    if (!target) return source;

    const result = { ...target };

    for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            result[key] = deepMerge(target[key] || {}, source[key]);
        } else if (source[key] !== undefined) {
            result[key] = source[key];
        }
    }

    return result;
}

export default ConfigContext;
